import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Calculator, ChevronDown, ChevronUp, Droplets, HelpCircle } from "lucide-react";

import { ApiError } from "../../lib/api";
import { cantidad } from "../../lib/format";
import { useAvisoSalida } from "../../lib/useAvisoSalida";
import { useToast } from "../../components/Toast";
import { Badge, Button, Cargando, ConfirmDialog, ErrorCarga, TableWrap } from "../../components/ui";
import {
  estimarFaltantes,
  listarFaltantes,
  type CierreMensualRead,
  type FaltanteCierre,
  type MotivoNoEstimable,
} from "./api";
import { MESES } from "./estado";
import { enCurso, registrarTareaLanzada, useTareaCierre } from "./tareas";

const MOTIVO_TEXTO: Record<MotivoNoEstimable, string> = {
  sin_medidor: "Sin medidor instalado",
  sin_historial: "Sin consumos previos",
};

/**
 * Faltantes del cierre y su término medio (recorrido R2).
 *
 * Un contrato sin lectura levanta un `BLQ-001` y basta uno para dejar el
 * período BLOQUEADO. El Manual SISS (cap. 4) dice qué hacer: facturar el
 * consumo promedio. Este panel muestra la propuesta casa por casa y la
 * aplica en lote. Con cientos de casas la lista se muestra recortada para
 * no empujar el resto de la pantalla.
 */
const FILAS_INICIALES = 10;

export function FaltantesPanel({
  cierre,
}: {
  cierre: CierreMensualRead;
}): JSX.Element | null {
  const toast = useToast();
  const qc = useQueryClient();
  const [confirmar, setConfirmar] = useState(false);
  const [excluidos, setExcluidos] = useState<Set<number>>(new Set());
  const [verTodas, setVerTodas] = useState(false);

  const faltantesQ = useQuery({
    queryKey: ["cierre-faltantes", cierre.id],
    queryFn: () => listarFaltantes(cierre.id),
  });

  const estimables = useMemo(
    () => (faltantesQ.data ?? []).filter((f) => f.estimable),
    [faltantesQ.data],
  );
  const noEstimables = useMemo(
    () => (faltantesQ.data ?? []).filter((f) => !f.estimable),
    [faltantesQ.data],
  );
  const seleccionados = useMemo(
    () => estimables.filter((f) => !excluidos.has(f.contrato_id)),
    [estimables, excluidos],
  );
  const m3 = seleccionados.reduce((acc, f) => acc + f.termino_medio_m3, 0);

  // La estimación corre en el servidor (cientos de casas tardan minutos): el
  // POST sólo la lanza, el diálogo se cierra y el avance se ve en el panel
  // de la tarea, arriba en el cierre. Al terminar, ese panel refresca los
  // faltantes y avisa el resultado.
  const tareaQ = useTareaCierre(cierre.id);
  const tareaCorriendo = enCurso(tareaQ.data);
  const estimarM = useMutation({
    mutationFn: () =>
      estimarFaltantes(
        cierre.id,
        // Sólo mandamos la selección si el operador desmarcó alguna: así el
        // caso normal ("todas") no depende de una lista que puede quedar vieja.
        excluidos.size > 0 ? seleccionados.map((f) => f.contrato_id) : undefined,
      ),
    onSuccess: (tarea) => {
      registrarTareaLanzada(qc, tarea);
      setExcluidos(new Set());
      setConfirmar(false);
    },
    onError: (err: unknown) => {
      setConfirmar(false);
      toast.error(
        err instanceof ApiError ? err.message : "No se pudo iniciar la estimación",
      );
      void tareaQ.refetch();
    },
  });

  // Sólo mientras se lanza: la tarea en sí ya no depende de esta pestaña.
  useAvisoSalida(estimarM.isPending);

  // Nada que estimar y nada que avisar: el panel no aporta.
  if (faltantesQ.data && faltantesQ.data.length === 0) return null;

  function toggle(contratoId: number): void {
    setExcluidos((prev) => {
      const next = new Set(prev);
      if (next.has(contratoId)) next.delete(contratoId);
      else next.add(contratoId);
      return next;
    });
  }

  return (
    <section className="bg-white rounded-lg border border-neutral-200">
      <header className="px-4 py-3 border-b border-neutral-100 flex flex-wrap items-start gap-3">
        <div>
          <h3 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
            <Droplets className="h-4 w-4" strokeWidth={1.5} />
            Casas sin lectura en {MESES[cierre.mes - 1]} {cierre.anio}
          </h3>
          <p className="text-xs text-neutral-600 mt-0.5">
            Si no se pudo leer el medidor, no se cobra cero: se cobra el
            consumo promedio de la casa. La boleta sale marcada como
            &ldquo;Término medio&rdquo; y el mes que se vuelva a leer el
            medidor, lo estimado ya queda descontado.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="primary"
            icon={Calculator}
            disabled={
              faltantesQ.isPending ||
              seleccionados.length === 0 ||
              estimarM.isPending ||
              tareaCorriendo
            }
            onClick={() => setConfirmar(true)}
          >
            {/* Mientras carga no hay "0 casas": se dice que está calculando. */}
            {estimarM.isPending || (tareaCorriendo && tareaQ.data?.tipo === "estimar_faltantes")
              ? "Estimando…"
              : faltantesQ.isPending
                ? "Calculando casas sin lectura…"
                : `Facturar ${cantidad(seleccionados.length)} por término medio`}
          </Button>
        </div>
      </header>

      <div className="p-4 space-y-3">
        {faltantesQ.isPending && <Cargando inline />}
        {faltantesQ.isError && (
          <ErrorCarga
            error={faltantesQ.error}
            mensaje="No se pudieron cargar los faltantes."
            onReintentar={() => void faltantesQ.refetch()}
          />
        )}

        {seleccionados.length > 0 && (
          <p className="text-xs text-neutral-500">
            {cantidad(seleccionados.length)} casa(s) · {cantidad(m3)} m³ a facturar
            {excluidos.size > 0 && ` · ${excluidos.size} excluida(s)`}
          </p>
        )}

        {estimables.length > 0 && (
          <TableWrap label="Casas sin lectura">
            <table className="min-w-full divide-y divide-neutral-200 text-sm">
              <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th scope="col" className="px-3 py-2 text-left w-8" />
                  <th scope="col" className="px-3 py-2 text-left">Socio</th>
                  <th scope="col" className="px-3 py-2 text-left">Arranque</th>
                  <th scope="col" className="px-3 py-2 text-right">Anterior</th>
                  <th scope="col" className="px-3 py-2 text-right">Término medio</th>
                  <th scope="col" className="px-3 py-2 text-right">Quedará en</th>
                  <th scope="col" className="px-3 py-2 text-left">Base</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {(verTodas ? estimables : estimables.slice(0, FILAS_INICIALES)).map((f) => (
                  <FaltanteRow
                    key={f.contrato_id}
                    faltante={f}
                    incluido={!excluidos.has(f.contrato_id)}
                    onToggle={() => toggle(f.contrato_id)}
                  />
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}

        {estimables.length > FILAS_INICIALES && (
          <Button
            variant="link"
            size="sm"
            icon={verTodas ? ChevronUp : ChevronDown}
            aria-expanded={verTodas}
            onClick={() => setVerTodas((v) => !v)}
          >
            {verTodas
              ? `Mostrar sólo las primeras ${FILAS_INICIALES}`
              : `Ver las ${estimables.length.toLocaleString("es-CL")}`}
          </Button>
        )}

        {noEstimables.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
            <div className="text-xs font-semibold text-amber-900 flex items-center gap-1.5">
              <HelpCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
              {noEstimables.length} caso(s) que hay que resolver a mano
            </div>
            <p className="text-xs text-amber-800 mt-0.5">
              No tienen con qué calcular un promedio, así que siguen bloqueando
              el cierre: hay que instalar el medidor o cargarles la lectura.
            </p>
            <ul className="mt-2 max-h-60 space-y-1 overflow-y-auto">
              {noEstimables.map((f) => (
                <li key={f.contrato_id} className="text-xs text-amber-900">
                  <span className="font-medium">{f.socio_nombre}</span>
                  {f.arranque_codigo ? ` · ${f.arranque_codigo}` : ""}
                  {" — "}
                  {f.motivo_no_estimable
                    ? MOTIVO_TEXTO[f.motivo_no_estimable]
                    : "Sin datos"}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmar}
        tone="default"
        title="Facturar por término medio"
        confirmLabel={`Estimar ${cantidad(seleccionados.length)} lectura(s)`}
        loading={estimarM.isPending}
        loadingLabel="Iniciando…"
        description={
          <>
            Se van a crear {cantidad(seleccionados.length)} lecturas Estimadas por un
            total de {cantidad(m3)} m³ en {MESES[cierre.mes - 1]} {cierre.anio}. Cada una
            queda con el detalle del promedio que la originó y la boleta sale
            marcada como &ldquo;Término medio&rdquo;. Después hay que volver a
            revisar las lecturas. Corre en el servidor: puedes seguir trabajando
            y el avance queda en el cierre.
          </>
        }
        onConfirm={() => estimarM.mutate()}
        onCancel={() => setConfirmar(false)}
      />
    </section>
  );
}

function FaltanteRow({
  faltante,
  incluido,
  onToggle,
}: {
  faltante: FaltanteCierre;
  incluido: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <tr className={incluido ? "hover:bg-neutral-50" : "bg-neutral-50 opacity-60"}>
      <td className="px-3 py-2">
        <input
          type="checkbox"
          checked={incluido}
          onChange={onToggle}
          aria-label={`Incluir a ${faltante.socio_nombre}`}
          className="h-4 w-4 rounded border-neutral-450"
        />
      </td>
      <td className="px-3 py-2">
        <div className="font-medium text-neutral-900">{faltante.socio_nombre}</div>
        <div className="text-xs text-neutral-500">{faltante.socio_rut ?? ""}</div>
      </td>
      <td className="px-3 py-2 text-neutral-600">
        <div>{faltante.arranque_codigo ?? "—"}</div>
        <div className="text-xs text-neutral-500">
          {faltante.arranque_direccion ?? ""}
        </div>
      </td>
      <td className="px-3 py-2 text-right text-neutral-500 tabular-nums">
        {faltante.lectura_anterior}
      </td>
      <td className="px-3 py-2 text-right font-medium tabular-nums">
        {faltante.termino_medio_m3} m³
      </td>
      <td className="px-3 py-2 text-right text-neutral-600 tabular-nums">
        {faltante.lectura_estimada}
      </td>
      <td className="px-3 py-2">
        <span
          className="text-xs text-neutral-500"
          title={`Consumos: ${faltante.consumos_base.join(", ")}`}
        >
          {faltante.meses_base} mes(es)
        </span>
        {faltante.base_debil && (
          <Badge tone="amber" className="ml-1.5">
            Base corta
          </Badge>
        )}
      </td>
    </tr>
  );
}
